import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, ArrowRight, Library } from 'lucide-react';
import { heroData } from '../data/mockData';

export const Hero = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState('');

  // Check if user has an active session + load video script
  useEffect(() => {
    const token = localStorage.getItem('reader_token');
    const user = localStorage.getItem('reader_user');
    if (token && user) {
      setIsLoggedIn(true);
      try {
        const userData = JSON.parse(user);
        setUserName(userData.name?.split(' ')[0] || '');
      } catch (e) {
        // Invalid user data
      }
    }

    // Load video player script
    const existingScript = document.querySelector('script[src*="converteai.net/lib/js/smartplayer"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://scripts.converteai.net/lib/js/smartplayer-wc/v4/sdk.js';
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  const scrollToBook = () => {
    const element = document.getElementById('libro');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleMainCTA = () => {
    if (isLoggedIn) {
      navigate('/reader/biblioteca');
    } else {
      navigate('/reader/register');
    }
  };

  return (
    <section id="home" className="hero-section relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src={heroData.backgroundImage}
          alt="Spiritual background"
          className="w-full h-full object-cover object-top"
        />
        {/* Stronger overlay on mobile for readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/80 to-black/60 md:from-black/70 md:via-black/50 md:to-transparent"></div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-32 relative z-10">
        <div className="max-w-3xl">
          {/* Welcome back message for logged in users */}
          {isLoggedIn && (
            <div className="mb-4 inline-flex items-center gap-2 bg-amber-500/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <span className="text-amber-400 text-sm">👋 ¡Hola de nuevo, {userName}!</span>
            </div>
          )}
          
          <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-4 md:mb-6 leading-tight">
            {heroData.title}
          </h1>
          <p className="text-xl sm:text-2xl md:text-3xl text-amber-400 font-light mb-3 md:mb-4">
            {heroData.subtitle}
          </p>
          <p className="text-base sm:text-lg md:text-xl text-gray-200 mb-6 md:mb-8 max-w-2xl">
            {heroData.description}
          </p>
          
          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <button 
              onClick={handleMainCTA}
              className="btn-primary group inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-xl"
            >
              {isLoggedIn ? (
                <>
                  <Library size={20} className="sm:w-6 sm:h-6" />
                  <span>Ir a Mi Biblioteca</span>
                </>
              ) : (
                <>
                  <BookOpen size={20} className="sm:w-6 sm:h-6" />
                  <span>Leer 30 páginas gratis</span>
                </>
              )}
              <ArrowRight className="group-hover:translate-x-1 transition-transform hidden sm:block" size={20} />
            </button>
            
            <button 
              onClick={scrollToBook}
              className="btn-secondary inline-flex items-center justify-center space-x-2 border-2 border-white text-white hover:bg-white hover:text-gray-900 px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold transition-all duration-300 transform hover:scale-105"
            >
              <span>Ver el libro</span>
            </button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
        <div className="flex flex-col items-center">
          <span className="text-white text-sm mb-2">Descubre más</span>
          <ArrowRight className="text-white opacity-70 rotate-90" size={32} />
        </div>
      </div>
    </section>
  );
};